import * as api from './api';
import { NotificationManager } from 'react-notifications';

export const normalizeTag = (tag) => {
  if (typeof tag === 'string') {
    return { name: tag, id: null };
  }
  return tag;
};

export const normalizeTagArray = (tags) => {
  if (!tags) return [];
  return tags.map(normalizeTag);
};

export const getTagName = (tag) => {
  return typeof tag === 'string' ? tag : tag.name;
};

export const extractTagsFromItems = (items) => {
  const allTags = new Set();
  items.forEach(item => {
    if (item.tags && Array.isArray(item.tags)) {
      item.tags.forEach(tag => {
        allTags.add(getTagName(tag));
      });
    }
  });
  return Array.from(allTags).map(name => ({ name, id: null }));
};

export const fetchAvailableTags = async (credentials, entityType = 'form') => {
  try {
    const getTagsFunction = entityType === 'form' ? api.getFormTags : 
                           entityType === 'user' ? api.getUserTags : 
                           (creds) => api.getTags(creds, entityType.toUpperCase());
    const tags = await getTagsFunction(credentials);
    return normalizeTagArray(tags);
  } catch (error) {
    console.log('Failed to fetch tags:', error);
    return [];
  }
};

export const createNewTags = async (newTagNames, credentials, entityType = 'form') => {
  const results = [];
  for (const tagName of newTagNames) {
    try {
      const createTagFunction = entityType === 'form' ? api.createFormTag : 
                               entityType === 'user' ? api.createUserTag : 
                               (data, creds) => api.createTag(data, creds, entityType.toUpperCase());
      await createTagFunction({ name: tagName }, credentials);
      results.push({ success: true, tagName });
    } catch (error) {
      console.log(`Tag "${tagName}" might already exist:`, error);
      results.push({ success: false, tagName, error });
    }
  }
  return results;
};

export const deleteTagsFromSystem = async (tagsToDelete, credentials) => {
  const results = [];
  for (const tag of tagsToDelete) {
    const tagId = typeof tag === 'string' ? null : tag.id;
    if (tagId) {
      try {
        await api.deleteTag(tagId, credentials);
        results.push({ success: true, tag });
      } catch (error) {
        console.log(`Failed to delete tag "${getTagName(tag)}":`, error);
        results.push({ success: false, tag, error });
      }
    }
  }
  return results;
};

export const updateItemTags = async (item, selectedTags, credentials, entityType = 'form', updateEndpointType = null) => {
  // Use updateEndpointType if provided, otherwise use entityType
  const endpointType = updateEndpointType || entityType;
  
  const updateFunctionMap = {
    form: api.updateFormTags,
    user: api.updateUserTags,
    report: api.updateReportTags
  };

  const updateFunction = updateFunctionMap[endpointType];
  if (!updateFunction) {
    throw new Error(`Unsupported endpoint type: ${endpointType}`);
  }

  return await updateFunction(item.id, selectedTags, credentials);
};

export const saveTags = async (item, selectedTags, allAvailableTags, tagsToDelete = [], credentials, entityType = 'form', currentTagNames = [], updateEndpointType = null) => {
  try {
    await deleteTagsFromSystem(tagsToDelete, credentials);

    // Convert allAvailableTags to tag names in case they're tag objects
    const allAvailableTagNames = allAvailableTags.map(tag => 
      typeof tag === 'string' ? tag : getTagName(tag)
    );

    const newTagNames = allAvailableTagNames.filter(tagName => 
      !currentTagNames.includes(tagName)
    );

    if (newTagNames.length > 0) {
      await createNewTags(newTagNames, credentials, entityType);
    }

    await updateItemTags(item, selectedTags, credentials, entityType, updateEndpointType);
    
    NotificationManager.success('Tags updated successfully');
    
    return { success: true };
  } catch (error) {
    NotificationManager.error('Failed to update tags');
    return { success: false, error };
  }
};

export const filterItemsByTag = (items, selectedTag) => {
  if (!selectedTag || selectedTag === 'all') return items;
  
  return items.filter(item => {
    if (!item.tags) return false;
    return item.tags.some(tag => getTagName(tag) === selectedTag);
  });
};

export const createTagFilter = (selectedTag) => {
  if (!selectedTag || selectedTag === 'all') {
    return () => true;
  }
  
  return (item) => {
    if (!item.tags) return false;
    return item.tags.some(tag => getTagName(tag) === selectedTag);
  };
};

export const updateItemsAfterTagChanges = (items, targetItem, selectedTags, tagsToDelete = []) => {
  const deletedTagNames = tagsToDelete.map(getTagName);
  
  return items.map(item => {
    let updatedTags = normalizeTagArray(item.tags);
    
    if (deletedTagNames.length > 0) {
      updatedTags = updatedTags.filter(tag => 
        !deletedTagNames.includes(getTagName(tag))
      );
    }
    
    if (item.id === targetItem.id) {
      updatedTags = selectedTags.map(tagName => ({ name: tagName, id: null }));
    }
    
    return { ...item, tags: updatedTags };
  });
};

export const normalizeItemForEditing = (item) => {
  if (!item) return null;
  return {
    ...item,
    tags: normalizeTagArray(item.tags).map(getTagName)
  };
};